import { Dispatch } from 'redux';
import types from 'redux/actions/actionTypes';
import { axiosV2 } from 'interceptors/axios';

import {
  IncentiveOfferOperation,
  IncentiveOfferOperationDocumentRequest,
} from 'modules/incentivesMarket/types';
import { PublisherAuditClientContext } from 'modules/incentivesMarket/utils/collectPublisherAuditContext';

import { decryptResponse } from 'utils/token';
import { dispatchError } from 'utils/dispatchError';
import { ApiQueryParams } from 'types/ApiQueryParams';

type IncentiveOfferOperationApiGetAllResponse = {
  data: IncentiveOfferOperation[];
  total: number;
};

type BuyerUpdateData = {
  name?: string;
  cif?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    zip?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  bankAccountNumber?: string;
};

type CreateIncentiveOfferOperationPayload = Partial<IncentiveOfferOperation> & {
  participantAuditContext?: PublisherAuditClientContext;
  buyerUpdateData?: BuyerUpdateData;
  domain?: string;
};

export const getIncentiveOfferOperations =
  (
    params: {
      incentiveOfferId?: string;
      societyId?: string;
      seller?: string | string[];
    } & ApiQueryParams
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get('/incentiveOfferOperations', {
        params,
      });

      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperationApiGetAllResponse;

      dispatch({
        type: types.GET_INCENTIVE_OFFER_OPERATIONS,
        incentiveOfferOperations: decodedData.data,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const getIncentiveOfferOperation =
  (operationId: string, params?: ApiQueryParams) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get(
        `/incentiveOfferOperations/${operationId}`,
        { params }
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperation;

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const createIncentiveOfferOperation =
  (operationData: CreateIncentiveOfferOperationPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.post(
        '/incentiveOfferOperations',
        operationData
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperation;

      dispatch({
        type: types.CREATE_INCENTIVE_OFFER_OPERATION,
        incentiveOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const updateIncentiveOfferOperation =
  (operationId: string, operationData: Partial<IncentiveOfferOperation>) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.put(
        `/incentiveOfferOperations/${operationId}`,
        operationData
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperation;

      dispatch({
        type: types.UPDATE_INCENTIVE_OFFER_OPERATION,
        incentiveOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const requestIncentiveOfferOperationDocuments =
  (
    operationId: string,
    body: Pick<
      IncentiveOfferOperationDocumentRequest,
      'documentType' | 'description' | 'deadlineBusinessDays' | 'targetRole'
    >
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.post(
        `/incentiveOfferOperations/${operationId}/request-documents`,
        body
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperation;

      dispatch({
        type: types.UPDATE_INCENTIVE_OFFER_OPERATION,
        incentiveOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const postIncentiveOfferOperationComment =
  (
    operationId: string,
    body: { text: string; asBeneficiaryId?: string }
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.post(
        `/incentiveOfferOperations/${operationId}/comments`,
        body
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperation;

      dispatch({
        type: types.UPDATE_INCENTIVE_OFFER_OPERATION,
        incentiveOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const deleteIncentiveOfferOperationComment =
  (operationId: string, commentId: string) => async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.delete(
        `/incentiveOfferOperations/${operationId}/comments/${commentId}`
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferOperation;

      dispatch({
        type: types.UPDATE_INCENTIVE_OFFER_OPERATION,
        incentiveOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const deleteIncentiveOfferOperation =
  (operationId: string) => async (dispatch: Dispatch) => {
    try {
      await axiosV2.delete(`/incentiveOfferOperations/${operationId}`);

      dispatch({
        type: types.DELETE_INCENTIVE_OFFER_OPERATION,
        incentiveOfferOperationId: operationId,
      });
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };
